/**
 * AllStocksPage — every stock on a US exchange (~5,594 names) in a single
 * sortable / searchable table.  Universe is scraped from
 * stockanalysis.com/stocks/ via `/api/all-stocks/data`; the same endpoint
 * also kicks off a background refresh that computes 1D/5D/20D/50D/100D/200D
 * returns for every symbol via Yahoo v8 — the page polls `/status` while
 * that refresh is in-flight and re-renders rows as they fill in.
 *
 * Styling mirrors the GICS Sector & Sub-Sector Performance table:
 *   • black/[#111] surface, white body text
 *   • light-blue hyperlinks (ticker / Yahoo Finance)
 *   • green % gains, red % losses
 *   • sortable column headers with a chevron indicator on the active column
 */
import { useEffect, useMemo, useState, useCallback } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import {
  Search, RefreshCw, ChevronDown, ChevronUp, ExternalLink,
  ChevronLeft, ChevronRight, Layers, Download,
} from "lucide-react";
import { API } from "../config/api";
import AIAnalysisChat from "../components/AIAnalysisChat";

const PAGE_SIZE_OPTIONS = [50, 100, 200, 500];
const RETURN_COLUMNS = [
  { key: "ret_1d",   label: "1D"   },
  { key: "ret_5d",   label: "5D"   },
  { key: "ret_10d",  label: "10D"  },
  { key: "ret_20d",  label: "20D"  },
  { key: "ret_50d",  label: "50D"  },
  { key: "ret_100d", label: "100D" },
  { key: "ret_200d", label: "200D" },
  { key: "ret_1y",   label: "1Y"   },
];

const fmtPct = (v) => (v == null ? "—" : `${v >= 0 ? "+" : ""}${Number(v).toFixed(2)}%`);
const pctClass = (v) =>
  v == null ? "text-gray-500"
  : v >= 0  ? "text-green-400"
            : "text-red-400";
const fmtPrice = (v) => (v == null ? "—" : `$${Number(v).toFixed(2)}`);
const fmtMcap = (v) => {
  if (v == null) return "—";
  if (v >= 1e12) return `$${(v / 1e12).toFixed(2)}T`;
  if (v >= 1e9)  return `$${(v / 1e9).toFixed(2)}B`;
  if (v >= 1e6)  return `$${(v / 1e6).toFixed(2)}M`;
  return `$${v.toFixed(0)}`;
};

export default function AllStocksPage() {
  const [rows, setRows] = useState([]);
  const [refreshState, setRefreshState] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // UI state
  const [search, setSearch] = useState("");
  const [industryFilter, setIndustryFilter] = useState("");
  // Multi-sort: array of {key, dir} up to 7 entries.  First entry is
  // the primary sort, second is tiebreaker, and so on.  Shift-click a
  // header to add a secondary sort; plain click sets it as the primary.
  const [sortSpecs, setSortSpecs] = useState([{ key: "ret_1d", dir: "desc" }]);
  const MAX_SORTS = 7;
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await axios.get(`${API}/all-stocks/data`, { timeout: 90000 });
      setRows(r.data?.rows || []);
      setRefreshState(r.data?.refresh_state || null);
    } catch (e) {
      setError(e?.message || "Failed to load.");
    } finally {
      setLoading(false);
    }
  }, []);

  const pollStatus = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/all-stocks/status`, { timeout: 8000 });
      setRefreshState(r.data || null);
    } catch (_e) { /* swallow — transient */ }
  }, []);

  // Initial bulk load
  useEffect(() => { loadData(); }, [loadData]);

  // While the backend is busy filling in returns, poll status every 5 s and
  // re-fetch the full payload every 15 s so the table progressively fills.
  useEffect(() => {
    if (!refreshState?.running) return undefined;
    let cycles = 0;
    const id = setInterval(async () => {
      cycles += 1;
      await pollStatus();
      if (cycles % 3 === 0) {
        // every 15 s pull the merged data so price/return cells refresh
        await loadData();
      }
    }, 5000);
    return () => clearInterval(id);
  }, [refreshState?.running, pollStatus, loadData]);

  // Distinct industries (sorted alpha) for the filter dropdown
  const industries = useMemo(() => {
    const s = new Set();
    rows.forEach(r => { if (r.industry) s.add(r.industry); });
    return Array.from(s).sort();
  }, [rows]);

  // Apply filter + search
  const filtered = useMemo(() => {
    const q = search.trim().toUpperCase();
    return rows.filter(r => {
      if (industryFilter && r.industry !== industryFilter) return false;
      if (!q) return true;
      return (
        (r.symbol || "").toUpperCase().includes(q) ||
        (r.name || "").toUpperCase().includes(q)
      );
    });
  }, [rows, search, industryFilter]);

  // Multi-sort — walk sortSpecs in order, first non-zero comparison wins.
  // Nulls always sink so the top of the table is meaningful.
  const sorted = useMemo(() => {
    const arr = [...filtered];
    const cmpOne = (a, b, key, dir) => {
      const av = a[key];
      const bv = b[key];
      const aNull = av == null;
      const bNull = bv == null;
      if (aNull && bNull) return 0;
      if (aNull) return 1;
      if (bNull) return -1;
      if (typeof av === "string" && typeof bv === "string") {
        return dir === "desc" ? bv.localeCompare(av) : av.localeCompare(bv);
      }
      return dir === "desc" ? bv - av : av - bv;
    };
    arr.sort((a, b) => {
      for (const s of sortSpecs) {
        const c = cmpOne(a, b, s.key, s.dir);
        if (c !== 0) return c;
      }
      return 0;
    });
    return arr;
  }, [filtered, sortSpecs]);

  // Pagination
  const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize));
  const safePage = Math.min(page, totalPages);
  const pageRows = useMemo(
    () => sorted.slice((safePage - 1) * pageSize, safePage * pageSize),
    [sorted, safePage, pageSize]
  );

  // Header click handler:
  //  • plain click on the PRIMARY sort  → cycle desc → asc → remove
  //  • plain click on a NEW column      → make it the sole primary (desc)
  //  • shift+click                      → add or update as an extra sort
  //    key (up to MAX_SORTS = 7 total).  Cycles direction if key exists.
  const onHeaderClick = (key, ev) => {
    const shift = ev?.shiftKey;
    setSortSpecs(prev => {
      const idx = prev.findIndex(s => s.key === key);
      if (shift) {
        if (idx < 0) {
          if (prev.length >= MAX_SORTS) return prev;
          return [...prev, { key, dir: "desc" }];
        }
        // Cycle direction of existing shift-added key; second cycle removes.
        const nextDir = prev[idx].dir === "desc" ? "asc" : null;
        if (nextDir === null) return prev.filter(s => s.key !== key);
        return prev.map(s => s.key === key ? { ...s, dir: nextDir } : s);
      }
      // Plain click
      if (idx === 0) {
        // Primary — cycle desc → asc → remove
        const nextDir = prev[0].dir === "desc" ? "asc" : null;
        if (nextDir === null) return prev.slice(1);
        return [{ key, dir: nextDir }, ...prev.slice(1)];
      }
      // New primary (drops all others).  Use plain click to fully reset;
      // use shift-click when you want to preserve secondary sorts.
      return [{ key, dir: "desc" }];
    });
    setPage(1);
  };
  // Lookup by key for header rendering — {dir, priority(1-based)} or null.
  const sortByKey = useMemo(() => {
    const m = {};
    sortSpecs.forEach((s, i) => { m[s.key] = { dir: s.dir, priority: i + 1 }; });
    return m;
  }, [sortSpecs]);

  const progressPct = refreshState && refreshState.total > 0
    ? Math.min(100, Math.round((refreshState.loaded / refreshState.total) * 100))
    : 100;

  // ── Export the FULL universe (all rows, all fields).
  //
  // Direct navigation via `window.location.assign(url)` is the most
  // reliable download trigger there is.  The browser sees the backend's
  // `Content-Disposition: attachment` header and downloads the file
  // in-place — no new tab (no popup blocker), no anchor `download`
  // attribute (no iframe sandbox concern), no JS Blob (no SmartScreen
  // false positive from `data:`/`blob:` URLs).
  //
  // Two formats:
  //   • .xlsx — formatted Excel workbook (openpyxl)
  //   • .csv  — universal fallback that no browser/proxy blocks
  const exportXlsxHref = `${API}/all-stocks/export.xlsx`;
  const exportCsvHref  = `${API}/all-stocks/export.csv`;
  const doDownload = useCallback((url) => {
    // Location assign triggers a top-level navigation; when the response
    // is Content-Disposition: attachment the current page stays put and
    // the browser saves the file.  This is the pattern used by GitHub's
    // "Download ZIP", S3 pre-signed URLs, and every enterprise dashboard.
    window.location.assign(url);
  }, []);

  // ── Compact universe snapshot fed into the AI chat's context.
  // Precomputes: top-N movers per timeframe + potential breakouts (strong
  // 20D+50D, positive 1D) + deteriorating (red across every window) +
  // recovering (strong 20D after weak 200D).  Keeps the payload < 4 KB.
  const universeSnapshot = useMemo(() => {
    if (!rows || rows.length === 0) return null;
    const topBy = (key, n = 8) => rows
      .filter(r => r[key] != null && r.market_cap != null && r.market_cap > 5e8)
      .sort((a, b) => (b[key] ?? 0) - (a[key] ?? 0))
      .slice(0, n)
      .map(r => ({ sym: r.symbol, name: r.name, val: r[key], mcap: r.market_cap }));
    const bottomBy = (key, n = 8) => rows
      .filter(r => r[key] != null && r.market_cap != null && r.market_cap > 5e8)
      .sort((a, b) => (a[key] ?? 0) - (b[key] ?? 0))
      .slice(0, n)
      .map(r => ({ sym: r.symbol, name: r.name, val: r[key], mcap: r.market_cap }));
    const breakouts = rows
      .filter(r => r.market_cap > 1e9 && r.ret_1d > 0 && r.ret_5d > 0 && r.ret_20d > 5 && r.ret_50d > 10)
      .sort((a, b) => (b.ret_50d ?? 0) - (a.ret_50d ?? 0))
      .slice(0, 10)
      .map(r => ({ sym: r.symbol, name: r.name, r5d: r.ret_5d, r20d: r.ret_20d, r50d: r.ret_50d }));
    const deteriorating = rows
      .filter(r => r.market_cap > 1e9 && r.ret_1d < 0 && r.ret_5d < 0 && r.ret_20d < 0 && r.ret_50d < 0 && r.ret_200d < 0)
      .sort((a, b) => (a.ret_50d ?? 0) - (b.ret_50d ?? 0))
      .slice(0, 10)
      .map(r => ({ sym: r.symbol, name: r.name, r20d: r.ret_20d, r50d: r.ret_50d, r200d: r.ret_200d }));
    const recovering = rows
      .filter(r => r.market_cap > 1e9 && r.ret_20d > 8 && r.ret_200d != null && r.ret_200d < -10)
      .sort((a, b) => (b.ret_20d ?? 0) - (a.ret_20d ?? 0))
      .slice(0, 10)
      .map(r => ({ sym: r.symbol, name: r.name, r20d: r.ret_20d, r200d: r.ret_200d }));
    return {
      universe_size: rows.length,
      top_1d:   topBy("ret_1d"),
      top_5d:   topBy("ret_5d"),
      top_20d:  topBy("ret_20d"),
      top_50d:  topBy("ret_50d"),
      top_1y:   topBy("ret_1y"),
      worst_1d: bottomBy("ret_1d"),
      worst_1y: bottomBy("ret_1y"),
      breakouts,
      deteriorating,
      recovering,
    };
  }, [rows]);

  return (
    <div className="min-h-screen bg-black text-white p-2" data-testid="all-stocks-page">
      {/* AI Chat — moved to top of page */}
      <div className="mb-2">
        <AIAnalysisChat
          mode="universe-stocks"
          extraContext={universeSnapshot || { universe_size: rows?.length ?? 0 }}
        />
      </div>
      <div className="bg-[#111] border border-gray-800 rounded">
        {/* Header */}
        <div className="bg-gradient-to-r from-[#1a1a2e] to-[#111] px-4 py-3 border-b border-gray-800 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <Layers className="w-4 h-4 text-purple-500" />
            <span className="text-sm font-semibold text-white">All Stocks</span>
            <span className="text-[10px] text-gray-500 ml-2">
              {rows.length.toLocaleString()} symbols · source: stockanalysis.com
            </span>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            {refreshState?.running ? (
              <div className="flex items-center gap-2 text-[10px] text-gray-300" data-testid="all-stocks-progress">
                <RefreshCw className="w-3 h-3 animate-spin text-purple-400" />
                <span className="font-mono">
                  Loading returns {refreshState.loaded}/{refreshState.total} ({progressPct}%)
                </span>
                <div className="w-24 h-1.5 bg-gray-800 rounded overflow-hidden">
                  <div
                    className="h-full bg-purple-500 transition-all"
                    style={{ width: `${progressPct}%` }}
                  />
                </div>
              </div>
            ) : (
              <span className="text-[10px] text-gray-500 font-mono">
                {refreshState?.loaded || 0} of {rows.length || refreshState?.total || 0} returns cached
              </span>
            )}
            <div className="inline-flex rounded overflow-hidden border border-emerald-700" data-testid="all-stocks-export-group">
              <button
                type="button"
                onClick={() => doDownload(exportXlsxHref)}
                disabled={rows.length === 0}
                className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-1 bg-emerald-800/40 text-emerald-100 hover:bg-emerald-700/60 disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="all-stocks-export"
                title={`Download all ${rows.length.toLocaleString()} stocks as Excel (.xlsx)`}
              >
                <Download className="w-3 h-3" />
                Excel
              </button>
              <button
                type="button"
                onClick={() => doDownload(exportCsvHref)}
                disabled={rows.length === 0}
                className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-1 bg-emerald-900/40 text-emerald-100 hover:bg-emerald-700/60 disabled:opacity-40 disabled:cursor-not-allowed border-l border-emerald-700"
                data-testid="all-stocks-export-csv"
                title={`Download all ${rows.length.toLocaleString()} stocks as CSV (opens in Excel)`}
              >
                CSV
              </button>
            </div>
            <button
              onClick={loadData}
              disabled={loading}
              className="text-gray-400 hover:text-white p-1 rounded hover:bg-gray-800"
              data-testid="all-stocks-refresh"
              title="Refresh"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
            </button>
          </div>
        </div>

        {/* Controls */}
        <div className="px-4 py-2 border-b border-gray-800 flex items-center gap-3 flex-wrap bg-[#0d0d0d]">
          <div className="flex items-center gap-1.5 bg-black border border-gray-700 rounded px-2 py-1 flex-1 min-w-[220px] max-w-[420px]">
            <Search className="w-3 h-3 text-gray-500" />
            <input
              type="text"
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              placeholder="Search symbol or company name..."
              className="bg-transparent outline-none text-[11px] text-white placeholder-gray-600 w-full"
              data-testid="all-stocks-search"
            />
          </div>
          <select
            value={industryFilter}
            onChange={(e) => { setIndustryFilter(e.target.value); setPage(1); }}
            className="bg-black border border-gray-700 text-white text-[11px] rounded px-2 py-1"
            data-testid="all-stocks-industry-filter"
          >
            <option value="">All industries ({industries.length})</option>
            {industries.map(ind => (
              <option key={ind} value={ind}>{ind}</option>
            ))}
          </select>
          <div className="flex items-center gap-1 ml-auto text-[10px] text-gray-400">
            Rows per page:
            <select
              value={pageSize}
              onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}
              className="bg-black border border-gray-700 text-white text-[11px] rounded px-2 py-1"
              data-testid="all-stocks-page-size"
            >
              {PAGE_SIZE_OPTIONS.map(n => <option key={n} value={n}>{n}</option>)}
            </select>
            <span className="ml-2 font-mono">
              {sorted.length.toLocaleString()} matches · page {safePage}/{totalPages}
            </span>
          </div>
        </div>

        {/* Sort buttons (mirroring the GICS-style timeframe pills).
            Shift-click adds as a secondary sort (up to 7 total).
            Clicking the primary again cycles direction. */}
        <div className="px-4 py-2 border-b border-gray-800 flex items-center gap-2 flex-wrap bg-[#0d0d0d]">
          <span className="text-[10px] text-gray-500 uppercase tracking-wider mr-1">
            Sort by <span className="text-gray-600 normal-case tracking-normal ml-1">(shift+click for secondary, up to 7)</span>
          </span>
          {RETURN_COLUMNS.map(c => {
            const info = sortByKey[c.key];
            return (
              <button
                key={c.key}
                onClick={(e) => onHeaderClick(c.key, e)}
                data-testid={`all-stocks-sort-${c.key}`}
                className={`px-2 py-1 text-[10px] font-medium rounded-md transition-all flex items-center gap-1
                  ${info ? "bg-purple-600 text-white" : "bg-gray-900 text-gray-400 hover:text-white"}`}
              >
                {c.label}
                {info && (info.dir === "desc"
                  ? <ChevronDown className="w-3 h-3" />
                  : <ChevronUp className="w-3 h-3" />)}
                {info && sortSpecs.length > 1 && (
                  <span className="ml-0.5 text-[9px] font-mono bg-black/40 rounded px-1 leading-none py-[1px]">{info.priority}</span>
                )}
              </button>
            );
          })}
          {sortSpecs.length > 1 && (
            <button
              onClick={() => { setSortSpecs([{ key: "ret_1d", dir: "desc" }]); setPage(1); }}
              data-testid="all-stocks-sort-reset"
              className="ml-2 px-2 py-1 text-[10px] font-medium rounded-md bg-gray-800 text-gray-300 hover:bg-gray-700 hover:text-white transition-all"
            >
              Reset sorts
            </button>
          )}
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full text-[11px] min-w-[1100px]">
            <thead>
              <tr className="text-[9px] text-gray-500 uppercase tracking-wider border-b border-gray-800 bg-[#0d0d0d]">
                <th
                  className="px-3 py-2 text-left cursor-pointer hover:text-white"
                  onClick={(e) => onHeaderClick("symbol", e)}
                  data-testid="header-symbol"
                >
                  <span className="inline-flex items-center gap-1">
                    Symbol
                    {sortByKey["symbol"] && (sortByKey["symbol"].dir === "desc" ? <ChevronDown className="w-3 h-3" /> : <ChevronUp className="w-3 h-3" />)}
                    {sortByKey["symbol"] && sortSpecs.length > 1 && <span className="ml-0.5 text-[8px] font-mono bg-purple-800/70 rounded px-1">{sortByKey["symbol"].priority}</span>}
                  </span>
                </th>
                <th
                  className="px-3 py-2 text-left cursor-pointer hover:text-white"
                  onClick={(e) => onHeaderClick("name", e)}
                  data-testid="header-name"
                >
                  <span className="inline-flex items-center gap-1">
                    Company
                    {sortByKey["name"] && (sortByKey["name"].dir === "desc" ? <ChevronDown className="w-3 h-3" /> : <ChevronUp className="w-3 h-3" />)}
                    {sortByKey["name"] && sortSpecs.length > 1 && <span className="ml-0.5 text-[8px] font-mono bg-purple-800/70 rounded px-1">{sortByKey["name"].priority}</span>}
                  </span>
                </th>
                <th
                  className="px-3 py-2 text-left cursor-pointer hover:text-white"
                  onClick={(e) => onHeaderClick("industry", e)}
                  data-testid="header-industry"
                >
                  <span className="inline-flex items-center gap-1">
                    Industry
                    {sortByKey["industry"] && (sortByKey["industry"].dir === "desc" ? <ChevronDown className="w-3 h-3" /> : <ChevronUp className="w-3 h-3" />)}
                    {sortByKey["industry"] && sortSpecs.length > 1 && <span className="ml-0.5 text-[8px] font-mono bg-purple-800/70 rounded px-1">{sortByKey["industry"].priority}</span>}
                  </span>
                </th>
                <th
                  className="px-3 py-2 text-right cursor-pointer hover:text-white"
                  onClick={(e) => onHeaderClick("market_cap", e)}
                  data-testid="header-mcap"
                >
                  <span className="inline-flex items-center gap-1 justify-end w-full">
                    Mkt Cap
                    {sortByKey["market_cap"] && (sortByKey["market_cap"].dir === "desc" ? <ChevronDown className="w-3 h-3" /> : <ChevronUp className="w-3 h-3" />)}
                    {sortByKey["market_cap"] && sortSpecs.length > 1 && <span className="ml-0.5 text-[8px] font-mono bg-purple-800/70 rounded px-1">{sortByKey["market_cap"].priority}</span>}
                  </span>
                </th>
                <th
                  className="px-3 py-2 text-right cursor-pointer hover:text-white"
                  onClick={(e) => onHeaderClick("price", e)}
                  data-testid="header-price"
                >
                  <span className="inline-flex items-center gap-1 justify-end w-full">
                    Price
                    {sortByKey["price"] && (sortByKey["price"].dir === "desc" ? <ChevronDown className="w-3 h-3" /> : <ChevronUp className="w-3 h-3" />)}
                    {sortByKey["price"] && sortSpecs.length > 1 && <span className="ml-0.5 text-[8px] font-mono bg-purple-800/70 rounded px-1">{sortByKey["price"].priority}</span>}
                  </span>
                </th>
                {RETURN_COLUMNS.map(c => (
                  <th
                    key={c.key}
                    className={`px-3 py-2 text-right cursor-pointer hover:text-white ${sortByKey[c.key] ? "text-purple-300 font-bold" : ""}`}
                    onClick={(e) => onHeaderClick(c.key, e)}
                    data-testid={`header-${c.key}`}
                  >
                    <span className="inline-flex items-center gap-1 justify-end w-full">
                      {c.label}
                      {sortByKey[c.key] && (sortByKey[c.key].dir === "desc" ? <ChevronDown className="w-3 h-3" /> : <ChevronUp className="w-3 h-3" />)}
                      {sortByKey[c.key] && sortSpecs.length > 1 && <span className="ml-0.5 text-[8px] font-mono bg-purple-800/70 rounded px-1">{sortByKey[c.key].priority}</span>}
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {error && (
                <tr>
                  <td colSpan={11} className="px-3 py-6 text-center text-red-400 text-xs">
                    {error}
                  </td>
                </tr>
              )}
              {!error && loading && rows.length === 0 && (
                <tr>
                  <td colSpan={11} className="px-3 py-12 text-center text-gray-400 text-xs">
                    <RefreshCw className="w-5 h-5 animate-spin mx-auto mb-2 text-purple-500" />
                    Loading the full stock universe…
                  </td>
                </tr>
              )}
              {!error && !loading && pageRows.length === 0 && rows.length > 0 && (
                <tr>
                  <td colSpan={11} className="px-3 py-6 text-center text-gray-500 text-xs">
                    No matches.
                  </td>
                </tr>
              )}
              {pageRows.map((r) => (
                <tr
                  key={r.symbol}
                  className="border-b border-gray-900 hover:bg-gray-900/40 transition-colors"
                  data-testid={`all-stocks-row-${r.symbol}`}
                >
                  <td className="px-3 py-1.5 font-mono font-bold">
                    <Link
                      to={`/stock?ticker=${encodeURIComponent(r.symbol)}`}
                      className="text-sky-300 hover:text-sky-200 hover:underline"
                      data-testid={`row-${r.symbol}-symbol`}
                    >
                      {r.symbol}
                    </Link>
                  </td>
                  <td className="px-3 py-1.5 text-white truncate max-w-[260px]" title={r.name || ""}>
                    {r.name || "—"}
                  </td>
                  <td className="px-3 py-1.5 text-white truncate max-w-[200px]" title={r.industry || ""}>
                    {r.industry || "—"}
                  </td>
                  <td className="px-3 py-1.5 text-right text-white font-mono">{fmtMcap(r.market_cap)}</td>
                  <td className="px-3 py-1.5 text-right text-white font-mono">{fmtPrice(r.price)}</td>
                  {RETURN_COLUMNS.map(c => (
                    <td
                      key={c.key}
                      className={`px-3 py-1.5 text-right font-mono ${pctClass(r[c.key])}`}
                      data-testid={`row-${r.symbol}-${c.key}`}
                    >
                      {fmtPct(r[c.key])}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination footer */}
        <div className="px-4 py-2 border-t border-gray-800 flex items-center justify-between bg-[#0d0d0d]">
          <div className="text-[10px] text-gray-500 font-mono">
            Showing {pageRows.length ? (safePage - 1) * pageSize + 1 : 0}–{(safePage - 1) * pageSize + pageRows.length} of {sorted.length.toLocaleString()}
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setPage(1)}
              disabled={safePage <= 1}
              className="text-[10px] px-2 py-1 rounded border border-gray-700 text-gray-300 hover:bg-gray-800 disabled:opacity-30 disabled:cursor-not-allowed"
              data-testid="page-first"
            >
              First
            </button>
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={safePage <= 1}
              className="text-[10px] p-1 rounded border border-gray-700 text-gray-300 hover:bg-gray-800 disabled:opacity-30 disabled:cursor-not-allowed"
              data-testid="page-prev"
            >
              <ChevronLeft className="w-3 h-3" />
            </button>
            <span className="text-[10px] text-gray-300 font-mono px-2">{safePage} / {totalPages}</span>
            <button
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={safePage >= totalPages}
              className="text-[10px] p-1 rounded border border-gray-700 text-gray-300 hover:bg-gray-800 disabled:opacity-30 disabled:cursor-not-allowed"
              data-testid="page-next"
            >
              <ChevronRight className="w-3 h-3" />
            </button>
            <button
              onClick={() => setPage(totalPages)}
              disabled={safePage >= totalPages}
              className="text-[10px] px-2 py-1 rounded border border-gray-700 text-gray-300 hover:bg-gray-800 disabled:opacity-30 disabled:cursor-not-allowed"
              data-testid="page-last"
            >
              Last
            </button>
          </div>
        </div>

        {/* Footer */}
        <div className="px-4 py-2 border-t border-gray-800 text-[9px] text-gray-600 flex items-center justify-between bg-[#0d0d0d]">
          <span className="inline-flex items-center gap-1">
            <ExternalLink className="w-3 h-3" />
            Data: stockanalysis.com (universe) · Yahoo Finance (returns)
          </span>
          <span>Refresh state: {refreshState?.running ? "filling cache…" : "idle"}</span>
        </div>
      </div>
    </div>
  );
}
